/* eslint-disable no-param-reassign -- deuda real, documentada a proposito (Fase 7, F7-4):
   calculateEnergyConsumptionData/calculateVehicleData/setBuildingNotEstimatedEmissions/
   setBuildingEstimatedEmissions mutan su parametro y lo devuelven, un idioma "mutate-and-
   return" usado consistentemente en todo este modulo (no un accidente puntual). Los
   llamadores (ConstructionEquipmentSection.tsx, EnergyConsumptionSection.tsx,
   UrbanInfrastructureTool/index.tsx, BuildingConstructionSection.tsx y otros) siempre usan
   el valor de retorno, nunca dependen del efecto colateral en si mismo - pero
   setBuildingNotEstimatedEmissions/setBuildingEstimatedEmissions mutan subobjetos anidados
   (columns/floors/exteriorWalls/windows/interiorWalls/roofs), asi que una reescritura segura
   e inmutable exigiria clonar esa jerarquia completa, no solo un shallow spread - un cambio
   real a la logica de calculo de emisiones/costos sin cobertura de tests dedicada para
   verificarlo. Se prefiere dejarlo documentado como deuda real (candidato a una sesion
   dedicada con tests de Calculator ampliados) antes que arriesgar una regresion silenciosa
   en un modulo de negocio critico. */
import {
  BuildingConstruction,
  EnergyDataRow,
  EnergyEfficiencyDetails,
  VehicleDataRow,
} from '../../../../../services/types/Calculator';

export const calculateRawMaterial = (factor: number, quantity: number): number =>
  ((quantity * factor) / 1000) * (44 / 12);

export const calculateLandUseChange = (
  facaboveGBCarbon: number,
  belowGBCarbon: number,
  area: number
): number => (facaboveGBCarbon + belowGBCarbon) * area;

export const calculateLandUseChangeTransport = (
  facaboveGBCarbon: number,
  belowGBCarbon: number,
  area: number
): number => (facaboveGBCarbon + belowGBCarbon) * area;

export const calculateStreetLights = (
  bulbs: number,
  wattage: number,
  hours: number,
  factor: number
): number => ((bulbs * wattage * hours * 365) / 1000000) * factor;

export const calculateEnergyEfficiency = (
  numberUnits: number,
  wattageConvetional: number,
  wattageReplacement: number,
  hours: number,
  factor: number
): EnergyEfficiencyDetails => {
  const before = (numberUnits * wattageConvetional * hours * 365) / 1000;
  const after = (numberUnits * wattageReplacement * hours * 365) / 1000;
  const saving = before - after;

  const details: EnergyEfficiencyDetails = {
    kwhBefore: before,
    kwhAfter: after,
    totalSaving: saving,
    avoidedEmissions: (saving * factor) / 1000,
  };
  return details;
};

export const calculateEnergyConsumption = (
  numberUnits: number,
  wattageConvetional: number,
  wattageReplacement: number,
  hours: number,
  factor: number
): EnergyEfficiencyDetails => {
  const before = (numberUnits * wattageConvetional * hours * 365) / 1000;
  const after = (numberUnits * wattageReplacement * hours * 365) / 1000;
  const saving = before - after;

  const details: EnergyEfficiencyDetails = {
    kwhBefore: before,
    kwhAfter: after,
    totalSaving: saving,
    avoidedEmissions: (saving * factor) / 1000,
  };
  return details;
};

export const calculateFuelData = (
  factor: number,
  quantity: number,
  measurementSystemId: number
): number => {
  const perMTCO2 = measurementSystemId === 2 ? 0.0004536 : 0.001;

  return factor * quantity * perMTCO2;
};

export const calculateElectricityData = (factor: number, quantity: number): number =>
  // const perMTCO2 = measurementSystemId === 2 ? 0.0004536 : 0.001;

  factor * quantity;

export const calculateEnergyConsumptionData = (dataRow: EnergyDataRow): EnergyDataRow => {
  const gperMT = 1e-6;
  const CH4 = 25;
  const N20 = 298;

  /* if (dataRow.factorByCountry === false) {
    dataRow.emissionFactor = dataRow.emissionFactor * (44 / 12);
  } */

  /* if (dataRow.heatingValue !== null) {
    dataRow.heatingValue = dataRow.heatingValue / litersPerGallong;
  } */

  dataRow.totalEmission = (dataRow.value * dataRow.emissionFactor) / 1000;

  if (dataRow.heatingValue !== null) {
    dataRow.totalEmission *= dataRow.heatingValue;
  }

  // co2emission
  dataRow.ch4Value = dataRow.value;
  if (dataRow.heatingValue !== null) {
    dataRow.ch4Value *= dataRow.heatingValue;
  }
  dataRow.co2Emission = 0;
  dataRow.ch4Emission = null;
  if (dataRow.ch4Factor !== null) {
    dataRow.ch4Emission = dataRow.ch4Value * dataRow.ch4Factor * CH4;

    dataRow.co2Emission += dataRow.ch4Emission;
  }
  dataRow.n2OEmission = null;
  if (dataRow.n2OFactor !== null) {
    dataRow.n2OEmission = dataRow.ch4Value * dataRow.n2OFactor * N20;
    dataRow.co2Emission += dataRow.n2OEmission;
  }

  dataRow.co2Emission *= gperMT;
  dataRow.totalEmission += dataRow.co2Emission;
  return dataRow;
};

export const calculateVehicleData = (dataRow: VehicleDataRow): VehicleDataRow => {
  const gperMT = 1e-6;
  const CH4 = 25;
  const N20 = 298;

  dataRow.co2Emission = dataRow.value * dataRow.co2Factor * gperMT;

  dataRow.totalEmission = dataRow.co2Emission;

  dataRow.ch4Emission = null;
  if (dataRow.ch4Factor !== null) {
    dataRow.ch4Emission = dataRow.value * dataRow.ch4Factor * gperMT * CH4;
    dataRow.totalEmission += dataRow.ch4Emission;
  }
  dataRow.n2OEmission = null;
  if (dataRow.n2OFactor !== null) {
    dataRow.n2OEmission = dataRow.value * dataRow.n2OFactor * gperMT * N20;
    dataRow.totalEmission += dataRow.n2OEmission;
  }

  return dataRow;
};

export const calculateRoadPavement = (factor: number, totalArea: number): number =>
  (factor * totalArea) / 1000;

export const calculateRoadPavementTransport = (
  result: number,
  totalAreaRoadRehabilitated: number,
  SqEmissionFactor: number
): number => result + (totalAreaRoadRehabilitated / 1000) * SqEmissionFactor;

export const calculateRoadMaintenance = (factor: number, totalArea: number): number =>
  (factor * totalArea) / 1000;

export const calculateEmissionsRoadMaintenanceActivities = (
  factor: number,
  totalArea: number
): number => (totalArea / 1000) * factor;

export const calculateEmissionsPurchasedElectricity = (
  countryFactor: number,
  amountElectricityConsumed: number
): number => amountElectricityConsumed * countryFactor;

export const setBuildingNotEstimatedEmissions = (
  buildingConstruction: BuildingConstruction,
  measurementSystemId: number
): BuildingConstruction => {
  const perMTCO2 = measurementSystemId === 2 ? 0.00045359237 : 0.001;

  if (buildingConstruction.totalArea) {
    buildingConstruction.columns.defaultArea =
      buildingConstruction.totalArea * buildingConstruction.columns.avgMaterial;

    buildingConstruction.columns.emissions =
      buildingConstruction.columns.defaultFactor *
      buildingConstruction.columns.defaultArea *
      perMTCO2;

    buildingConstruction.floors.defaultArea =
      buildingConstruction.totalArea * buildingConstruction.floors.avgMaterial;

    buildingConstruction.floors.emissions =
      buildingConstruction.floors.defaultFactor *
      buildingConstruction.floors.defaultArea *
      perMTCO2;

    buildingConstruction.exteriorWalls.defaultArea =
      buildingConstruction.totalArea * buildingConstruction.exteriorWalls.avgMaterial;

    buildingConstruction.exteriorWalls.emissions =
      buildingConstruction.exteriorWalls.defaultFactor *
      buildingConstruction.exteriorWalls.defaultArea *
      perMTCO2;

    buildingConstruction.windows.defaultArea =
      buildingConstruction.totalArea * buildingConstruction.windows.avgMaterial;

    buildingConstruction.windows.emissions =
      buildingConstruction.windows.defaultFactor *
      buildingConstruction.windows.defaultArea *
      perMTCO2;

    buildingConstruction.interiorWalls.defaultArea =
      buildingConstruction.totalArea * buildingConstruction.interiorWalls.avgMaterial;

    buildingConstruction.interiorWalls.emissions =
      buildingConstruction.interiorWalls.defaultFactor *
      buildingConstruction.interiorWalls.defaultArea *
      perMTCO2;

    buildingConstruction.roofs.defaultArea =
      buildingConstruction.totalArea * buildingConstruction.roofs.avgMaterial;

    buildingConstruction.roofs.emissions =
      buildingConstruction.roofs.defaultFactor * buildingConstruction.roofs.defaultArea * perMTCO2;
  }

  return buildingConstruction;
};

export const calculateBuildingNotEstimatedEmissions = (
  buildingConstruction: BuildingConstruction
): number => {
  const result =
    (buildingConstruction.columns && buildingConstruction.columns.emissions
      ? buildingConstruction.columns.emissions
      : 0) +
    (buildingConstruction.floors && buildingConstruction.floors.emissions
      ? buildingConstruction.floors.emissions
      : 0) +
    (buildingConstruction.exteriorWalls && buildingConstruction.exteriorWalls.emissions
      ? buildingConstruction.exteriorWalls.emissions
      : 0) +
    (buildingConstruction.windows && buildingConstruction.windows.emissions
      ? buildingConstruction.windows.emissions
      : 0) +
    (buildingConstruction.interiorWalls && buildingConstruction.interiorWalls.emissions
      ? buildingConstruction.interiorWalls.emissions
      : 0) +
    (buildingConstruction.roofs && buildingConstruction.roofs.emissions
      ? buildingConstruction.roofs.emissions
      : 0);

  return result;
};

export const setBuildingEstimatedEmissions = (
  buildingConstruction: BuildingConstruction,
  area: number,
  measurementSystemId: number
): BuildingConstruction => {
  const perMTCO2 = measurementSystemId === 2 ? 0.00045359237 : 0.001;

  if (area) {
    buildingConstruction.columns.defaultArea = area * buildingConstruction.columns.avgMaterial;

    buildingConstruction.columns.emissions =
      buildingConstruction.columns.defaultFactor *
      buildingConstruction.columns.defaultArea *
      perMTCO2;

    buildingConstruction.floors.defaultArea = area * buildingConstruction.floors.avgMaterial;

    buildingConstruction.floors.emissions =
      buildingConstruction.floors.defaultFactor *
      buildingConstruction.floors.defaultArea *
      perMTCO2;

    buildingConstruction.exteriorWalls.defaultArea =
      area * buildingConstruction.exteriorWalls.avgMaterial;

    buildingConstruction.exteriorWalls.emissions =
      buildingConstruction.exteriorWalls.defaultFactor *
      buildingConstruction.exteriorWalls.defaultArea *
      perMTCO2;

    buildingConstruction.windows.defaultArea = area * buildingConstruction.windows.avgMaterial;

    buildingConstruction.windows.emissions =
      buildingConstruction.windows.defaultFactor *
      buildingConstruction.windows.defaultArea *
      perMTCO2;

    buildingConstruction.interiorWalls.defaultArea =
      area * buildingConstruction.interiorWalls.avgMaterial;

    buildingConstruction.interiorWalls.emissions =
      buildingConstruction.interiorWalls.defaultFactor *
      buildingConstruction.interiorWalls.defaultArea *
      perMTCO2;

    buildingConstruction.roofs.defaultArea = area * buildingConstruction.roofs.avgMaterial;

    buildingConstruction.roofs.emissions =
      buildingConstruction.roofs.defaultFactor * buildingConstruction.roofs.defaultArea * perMTCO2;
  }

  return buildingConstruction;
};
